import TomSelect from "tom-select";

// Order-insensitive: TomSelect keeps `items` in the order the user picked them,
// while the server renders them in option order, so a reordering is not a change
// of value.
const sameValueSet = (a, b) => {
  if (a.length !== b.length) return false;
  const left = [...a].sort();
  const right = [...b].sort();
  return left.every((value, index) => value === right[index]);
};

// Which of an option's other names the current search matched, or null when the option's
// own name is what matched. Showing it answers "did this understand me?" without
// decorating results whose primary label already matches the query.
const matchedAlias = (data, query) => {
  const q = (query || "").trim().toLowerCase();
  if (!q || !data.aliases) return null;
  if ((data.text || "").toLowerCase().includes(q)) return null;

  return (
    data.aliases
      .split(",")
      .map((alias) => alias.trim())
      .find((alias) => alias.toLowerCase().includes(q)) || null
  );
};

// The gap between a control and its dropdown: `.ts-dropdown`'s margin-top in app.css.
const DROPDOWN_GAP = 6;

// Where a dropdown portalled to <body> sits: under its control, or above it when the
// viewport has too little room below and more above. It never runs past the bottom edge,
// which would scroll the page and shift an open modal. Opening upward anchors its bottom
// edge to the control, so a list that shrinks as the person types stays attached. The
// gap is set here rather than as a margin, because it moves to the other side.
const placeDropdown = (widget) => {
  const { control, dropdown } = widget;
  const rect = control.getBoundingClientRect();
  const viewport = document.documentElement.clientHeight;
  const below = viewport - rect.bottom;
  const up = below < dropdown.offsetHeight + DROPDOWN_GAP && rect.top > below;

  dropdown.style.margin = "0";
  dropdown.style.width = `${rect.width}px`;
  dropdown.style.left = `${rect.left + window.scrollX}px`;
  dropdown.style.top = up ? "auto" : `${rect.bottom + window.scrollY + DROPDOWN_GAP}px`;
  dropdown.style.bottom = up ? `${viewport - rect.top - window.scrollY + DROPDOWN_GAP}px` : "auto";
};

// TomSelect hides the native <select> and holds the chosen value in the DOM's
// selectedness property, which markup cannot express. Every LiveView patch walks
// the hidden <select> and morphdom rebuilds `selectedIndex` from the `selected`
// attribute alone, so the widget — not the freshly patched <select> — is the source
// of truth. We follow the server only when its requested value actually changes.
const TomSelectHook = {
  isUpdating: false,
  serverValues: [],

  mounted() {
    // Before the early return: `updated()` compares against this on every patch.
    this.serverValues = this.requestedValues();

    // A select wrapped in `phx-update="ignore"` (the job-board filters, so an
    // unrelated patch can't orphan the TomSelect DOM) never receives `updated()`,
    // so a *server*-driven value change — a "Clear" button, clear-all — can't
    // reach the widget through the markup. The server pushes `tomselect:sync`
    // with an {id => value} map instead; each widget applies its own entry
    // silently (no change event, so it never loops back into `phx-change`).
    // Registered before the early return below: a remount onto an element that
    // still carries `el.tomselect` exits there, but that widget is exactly the
    // one reachable only through this event, not the markup.
    this.handleEvent("tomselect:sync", (payload) => {
      if (!Object.prototype.hasOwnProperty.call(payload, this.el.id)) return;
      const value = payload[this.el.id];
      const list = Array.isArray(value) ? value : value === "" ? [] : [value];
      this.serverValues = list;
      if (this.tomSelect) this.tomSelect.setValue(value === "" ? [] : value, true);
    });

    if (this.el.tomselect) return;
    this.initSelect();
  },

  updated() {
    // Guard against re-entrant updates (setValue could otherwise re-trigger).
    if (this.isUpdating) return;
    this.isUpdating = true;

    try {
      const requested = this.requestedValues();
      const values = this.resolveValues(requested);
      this.rebuildPreservingOptions();
      // Silent: no change event, so this never loops back into `phx-change`. It also
      // repairs the `selectedIndex` morphdom clobbered, because `setValue` writes
      // the selection back onto the native <select> that the form serializes.
      this.tomSelect.setValue(values, true);
      // Last: `serverValues` records what we have *applied*, not what we were asked
      // for. Advancing it before the rebuild would make the retry after a throw read
      // as "unchanged" — and that branch trusts a widget which no longer exists, so
      // it would answer with an empty selection and wipe the user's pick.
      this.serverValues = requested;
    } finally {
      this.isUpdating = false;
    }
  },

  destroyed() {
    if (this._onChangeCapture) {
      this.el.removeEventListener("change", this._onChangeCapture, true);
      this._onChangeCapture = null;
    }
    this.unwatchModal();
    if (this.tomSelect) this.tomSelect.destroy();
  },

  // One set of modal listeners per hook, replaced on every rebuild so a patch never
  // stacks a second set on the modal. `scroll` is captured because it does not
  // bubble; `transitionend` bubbles up from the box's open animation.
  watchModal(modal, onMove) {
    this.unwatchModal();
    modal.addEventListener("scroll", onMove, { capture: true, passive: true });
    modal.addEventListener("transitionend", onMove);
    this._modalWatch = { modal, onMove };
  },

  unwatchModal() {
    if (!this._modalWatch) return;
    const { modal, onMove } = this._modalWatch;
    modal.removeEventListener("scroll", onMove, { capture: true });
    modal.removeEventListener("transitionend", onMove);
    this._modalWatch = null;
  },

  // Which values the rebuilt widget should carry, given what the server just asked
  // for. An unchanged request means this patch was rendered before the server saw
  // the user's pick, so the widget is the fresher of the two; a changed one is the
  // server genuinely deciding something. Does not mutate `serverValues` — the
  // caller owns that update.
  //
  // Known limit: a value the server *rejects* renders identically to one it has not
  // seen yet — both are simply absent from the markup — so a rejected pick stays
  // visible until the server sends a different value. Submit-time validation still
  // reports it. Distinguishing the two would mean guessing how many patches to wait
  // for an echo, which reintroduces the reset this hook exists to prevent.
  resolveValues(requested) {
    // Needs no filtering against the option list: `requestedValues()` builds this
    // by reading `selected` attributes *off* `this.el.options`, so every value in
    // it is by construction the value of an option currently in that list.
    if (!sameValueSet(requested, this.serverValues)) return requested;

    // The widget's own items carry no such guarantee. Drop anything the server has
    // since dropped from the option list, otherwise `setValue` ignores it and the
    // widget and the form disagree about the value.
    const available = new Set([...this.el.options].map((opt) => opt.value));
    return this.widgetValues().filter((value) => available.has(value));
  },

  // `destroy()` restores the <select>'s innerHTML from the snapshot TomSelect took
  // when it was constructed, which would discard the option list this patch just
  // delivered. Park the patched nodes in a fragment and put them back afterwards —
  // moving the nodes keeps their identity and skips re-parsing the option list,
  // which matters when a select carries hundreds of them.
  //
  // Both steps have to survive a throw, or the failure compounds on every later
  // patch: drop the reference before tearing down so a widget that fails to
  // destroy is never destroyed twice or read for its `items`, and restore the
  // parked nodes in a `finally` so a failed teardown can't leave the <select> the
  // form serializes empty.
  rebuildPreservingOptions() {
    const options = document.createDocumentFragment();
    options.append(...this.el.childNodes);

    const widget = this.tomSelect;
    this.tomSelect = null;

    try {
      if (widget) widget.destroy();
    } finally {
      // `destroy()` deletes `el.tomselect` as its very last step, so a teardown
      // that threw part-way leaves it set — and TomSelect's constructor refuses an
      // element still carrying it. Clear it here (a no-op when destroy succeeded)
      // so the next patch can rebuild, instead of wedging on "already initialized"
      // for the rest of the page's life.
      delete this.el.tomselect;
      this.el.replaceChildren(options);
    }

    this.initSelect();
  },

  // What the server asked for in the markup it just sent. Read the `selected`
  // attribute, not `option.selected`: for a select the server renders with no value,
  // the browser auto-selects the first <option> of the parsed markup, so morphdom
  // hands the *property* to the prompt. An absent attribute is what "the server has
  // no value for this field" actually looks like.
  requestedValues() {
    return [...this.el.options]
      .filter((opt) => opt.hasAttribute("selected"))
      .map((opt) => opt.value);
  },

  // What the widget is showing the user right now.
  widgetValues() {
    return this.tomSelect ? [...this.tomSelect.items] : [];
  },

  initSelect() {
    const el = this.el;
    const isMulti = el.multiple;

    // Remove the previous widget's capture listener before rebuilding. Each
    // initSelect() closes over its own `committing`, so a stale listener left on
    // the persistent <select> keeps `committing === false` forever and would
    // swallow the next widget's commit `change` (dispatched in onDropdownClose).
    if (this._onChangeCapture) {
      el.removeEventListener("change", this._onChangeCapture, true);
      this._onChangeCapture = null;
    }
    // Opt-in via `data-blur-on-select`: after a pick, drop focus so a single-select
    // reads as a settled value instead of an active, still-typeable text field.
    const blurOnSelect = el.dataset.blurOnSelect === "true";
    // Opt-in via `data-dropdown-parent`: render the dropdown under that element
    // (e.g. "body") so it escapes an ancestor's `overflow` clip or low z-index —
    // needed inside the filters popover/sheet, which both scroll their content.
    //
    // Inside a daisyUI `.modal` this is the default: `.modal-box` scrolls its
    // content (`overflow-y: auto`) and is scaled, so an in-place dropdown is
    // clipped at the box's edge. It goes to `body`, not to the `<dialog>`: the
    // dialog is LiveView-rendered, so the next patch of the form inside it would
    // discard a node the hook appended there. `data-over-modal` lifts it above
    // the modal's own z-index (see app.css).
    const modal = el.closest(".modal");
    const dropdownParent = el.dataset.dropdownParent || (modal ? "body" : null);
    // Opt-in via `data-commit-on-close`: keep the dropdown open across picks and
    // fire a single form `change` (→ `phx-change`) only when it closes, so a
    // multi-select that filters a result set runs one search per session of
    // picking, not one per pick.
    const commitOnClose = el.dataset.commitOnClose === "true";
    let committing = false;
    // Opt-in via `data-placeholder`: prompt text shown in the empty control that
    // TomSelect hides on its own once any item is selected. A `<select>` takes no
    // native `placeholder` attribute (it trips a browser a11y check), so carry it
    // on a data attribute instead.
    const placeholder = el.dataset.placeholder || null;
    // Opt-in via `data-search-event`: the <select> carries only its chosen option
    // (a list too long to ship, like airports), and TomSelect asks the LiveView for
    // matches as the user types. The event replies `%{options: [%{text, value}]}`.
    const searchEvent = el.dataset.searchEvent || null;
    // Opt-in via `data-search-blank`, beside `data-search-event`: a blank search box asks
    // the server too, so the list the <select> arrived with (what a blank query answers)
    // comes back after a search replaced it. TomSelect never loads an empty query on its
    // own, and the reply to the last search would otherwise stay on screen.
    const searchBlank = Boolean(searchEvent) && el.dataset.searchBlank === "true";
    const hook = this;

    const plugins = [];
    // Opt-in via `data-clearable`: an × that empties a single select. A searched
    // select shows no blank choice to pick, so this is the only way to unset it.
    if (el.dataset.clearable === "true" && !isMulti) plugins.push("clear_button");
    // Multi-selects get removable chips (remove_button) and a checkbox per
    // dropdown row (checkbox_options), matching the prototype's SimpleMultiSelect:
    // checkbox_options also flips `hideSelected` off so picks stay visible+checked.
    if (isMulti) plugins.push("remove_button", "checkbox_options");

    this.tomSelect = new TomSelect(el, {
      maxOptions: null,
      dropdownParent,
      // A modal focuses its first field as it opens. Opening the dropdown on that focus
      // covers the fields below it before the person has touched anything, so in a modal
      // the dropdown waits for a pointer (see below), typing, or an arrow key.
      openOnFocus: !modal,
      placeholder,
      // Drop the placeholder as soon as anything is picked, so it never shows
      // beside the selected chips.
      hidePlaceholder: placeholder ? true : undefined,
      // Keep the dropdown open after a pick when committing on close, so several
      // options can be chosen in one go.
      closeAfterSelect: commitOnClose ? false : undefined,
      // No yellow match-highlight: the prototype's dropdowns filter without
      // decorating the matched substring.
      highlight: false,
      plugins,
      // TomSelect copies an option's whole `dataset` onto the option it searches, so an
      // option carrying `data-aliases` gets an `aliases` field for free — no `dataAttr`
      // and no JSON. Searching it is what lets someone find a berth by their own word
      // for it: an employer types "chief officer" and reaches Chief Mate. The aliases
      // are deliberately NOT in the visible label — putting them there is what made one
      // option run to 138 characters in a control that cannot wrap.
      searchField: ["text", "aliases"],
      ...(searchEvent && {
        // TomSelect marks the wrapper with `loading` while a search is in flight, and
        // daisyUI's `.loading` is its spinner: a square, masked box. On the full-width
        // wrapper that turned the field into a tall blank square on every keystroke
        // until the reply landed (JOBS-1112). A name daisyUI does not use avoids it.
        loadingClass: "ts-loading",
        load(query, callback) {
          hook.pushEvent(searchEvent, { query }, (reply) => {
            // Drop the last search's matches (the chosen item stays), so only this
            // query's results are offered.
            this.clearOptions();
            // `clearOptions` also forgets which queries were answered, this one included.
            // Remember it, so reopening on a blank list does not ask for it again.
            if (searchBlank) this.loadedSearches[query] = true;
            callback(reply.options);
          });
        },
        // The server already matched and ranked these, on more than the label shows.
        // Scoring them locally would hide some and reorder the rest.
        score() {
          return () => 1;
        },
      }),
      // Emptying the search box now loads the blank list. Each reply clears TomSelect's
      // cache of loaded queries but the one it answers, so a blank query after a search is
      // asked for again.
      ...(searchBlank && { shouldLoad: () => true }),
      render: {
        // `this` is the TomSelect instance here (it calls render with `.call(this, …)`),
        // which is the only way to reach the live query — `render.option` is not given it.
        option(data, escape) {
          const alias = matchedAlias(data, this.lastValue);
          const suffix = alias ? ` <span class="opacity-50">(${escape(alias)})</span>` : "";
          return `<div>${escape(data.text)}${suffix}</div>`;
        },
        // An option carrying `data-selected-label` shows that shorter text once chosen, so
        // a long name ("Philippines (+63)") never runs under the caret. The list keeps the
        // full name, which is what the person searches by.
        item(data, escape) {
          return `<div>${escape(data.selectedLabel || data.text)}</div>`;
        },
      },
      // TomSelect caches each option's rendered element on `option.$div` and reuses it, so
      // a render that reads the query — as the matched-alias suffix above does — keeps
      // showing the alias matched by an *earlier* keystroke. Typing "chief officer" left
      // "Able Seaman (Deckhand)" on screen from the previous search. Dropping the cache and
      // refreshing re-renders every visible option against the current query, and doing it
      // here rather than in an input listener keeps it independent of listener order.
      onType() {
        this.clearCache();
        this.refreshOptions(false);
      },
      onItemAdd() {
        if (blurOnSelect) this.blur();
      },
      // Snapshot the selection when the dropdown opens, so a session that closes
      // with nothing picked (Escape, an outside click, a tab-away) can be told
      // apart from one that changed the selection.
      onDropdownOpen() {
        if (commitOnClose) this._selectionAtOpen = [...this.items];
        // Closing empties the search box without typing, so no input asked for the blank
        // list. Ask on reopen. `load` skips the query the list already answers, so this
        // sends nothing unless a search has replaced the list.
        if (searchBlank && this.inputValue() === "") this.load("");
      },
      // When committing on close, hold the search until the dropdown closes: the
      // per-pick change events are swallowed in `onInitialize`, so dispatch one
      // change now that carries the full selection to `phx-change`. Skip it when
      // the selection is unchanged, so an open-and-close never churns `phx-change`.
      onDropdownClose() {
        if (!commitOnClose) return;
        if (this._selectionAtOpen && sameValueSet([...this.items], this._selectionAtOpen)) return;
        committing = true;
        el.dispatchEvent(new Event("change", { bubbles: true }));
        committing = false;
      },
      onInitialize() {
        // Keep the original <select> hidden.
        el.style.display = "none";

        // The options the server rendered are its answer to a blank query, so mark that
        // query answered. The first open then shows them without asking again.
        if (searchBlank) this.loadedSearches[""] = true;

        // Don't let typing in the search box bubble to the form's `phx-change`
        // — that would round-trip to the server and churn the widget mid-search.
        this.control_input.addEventListener("input", (e) => e.stopPropagation());
        this.control_input.addEventListener("change", (e) => e.stopPropagation());

        // A single-select marks itself `ts-searching` while its search box holds text,
        // so app.css can hide the picked item. The search box otherwise gets only the
        // width the item leaves, and the typed text needs the whole control.
        // TomSelect fires `update` when it sets the text itself (clearing it on a pick
        // or on close), and `input` covers typing.
        if (!isMulti) {
          const input = this.control_input;
          const searching = () => this.wrapper.classList.toggle("ts-searching", input.value !== "");
          input.addEventListener("input", searching);
          input.addEventListener("update", searching);
        }

        // A *portalled* dropdown (`data-dropdown-parent="body"`, or any select in
        // a modal) renders its options OUTSIDE the popover or modal box, so an
        // option click reads as "outside" and a `phx-click-away` would dismiss on
        // it. Stop only the `click` from reaching the document (that is the event
        // `phx-click-away` listens on) — and only for portalled dropdowns. Do NOT
        // touch `mousedown`, and do NOT do this for in-DOM dropdowns: TomSelect
        // selects an option on the event bubbling through the dropdown, and a
        // blanket `stopPropagation` here swallowed a real click's selection on
        // every select in the app.
        if (dropdownParent) {
          this.dropdown.addEventListener("click", (e) => e.stopPropagation());
        }

        // Swallow the per-pick change events TomSelect fires on the <select> so
        // each pick doesn't reach `phx-change`; `onDropdownClose` re-fires one
        // change (with `committing` set) that is allowed through. Capture phase,
        // so it intercepts before the event bubbles to the form.
        if (commitOnClose) {
          this._onChangeCapture = (e) => {
            if (!committing) e.stopImmediatePropagation();
          };
          el.addEventListener("change", this._onChangeCapture, true);
        }
      },
    });

    // In a modal the dropdown sits in <body>. TomSelect places it on open and on
    // *window* scroll only, always below the control. Place it with `placeDropdown`
    // instead, so it can open upward, and again whenever `.modal-box` scrolls or ends
    // its open animation, so it lines up with where the control finally lands. A
    // pointer on the control lets later focus open the dropdown (see `openOnFocus`).
    if (modal && dropdownParent === "body") {
      const widget = this.tomSelect;
      widget.dropdown.dataset.overModal = "";
      widget.positionDropdown = () => placeDropdown(widget);
      widget.wrapper.addEventListener(
        "pointerdown",
        () => {
          widget.settings.openOnFocus = true;
        },
        { capture: true, once: true }
      );
      this.watchModal(modal, () => {
        if (widget.isOpen) widget.positionDropdown();
      });
    }
  },
};

export default TomSelectHook;
